import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { dimensionLabel, dimensionShort, rubricColor } from "../lib/scoring";
import MarkButton from "./MarkButton";
import ConfirmModal from "./ConfirmModal";

const DIMENSIONS = [
  "optimism", "emotional_regulation", "ownership", "flexibility",
  "social_support", "purpose", "grit",
];

/**
 * Gate 8 — Team comparison view.
 *
 * Two segments side by side. Useful for testing whether a specific
 * population (a team, a role, a tenure band) is meaningfully different
 * from the baseline (or from another segment).
 *
 * No new backend endpoint. We call /scores twice with the filter
 * payload for A and the filter payload for B, then compute deltas
 * client-side. n<5 suppression is honored by /scores at the wire
 * layer — a suppressed side carries no dimension data and renders
 * accordingly.
 *
 * URL state:
 *   ?compare_a=_all                 (the all-respondents baseline)
 *   ?compare_a=team:North
 *   ?compare_b=role:Manager
 */
export default function Compare({ deploymentId, demographicConfig, urlA, urlB, onSetUrl }) {
  // Build the list of pickable segments from the deployment's demographic_config.
  // _all is always available as the baseline.
  const segments = useMemo(() => {
    const list = [{ key: "_all", value: "All respondents", id: "_all" }];
    for (const [k, vs] of Object.entries(demographicConfig || {})) {
      for (const v of vs) {
        list.push({ key: k, value: v, id: `${k}:${v}` });
      }
    }
    return list;
  }, [demographicConfig]);

  // Default A is the baseline; default B is the first non-baseline segment.
  const aId = urlA || "_all";
  const bId = urlB || (segments.find((s) => s.id !== "_all")?.id ?? "_all");

  const segA = segments.find((s) => s.id === aId) || segments[0];
  const segB = segments.find((s) => s.id === bId) || segments[0];

  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revealed, setRevealed] = useState(() => new Set());
  const [revealTarget, setRevealTarget] = useState(null);
  const revealA = revealed.has(aId);
  const revealB = revealed.has(bId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [a, b] = await Promise.all([fetchScores(segA, revealA), fetchScores(segB, revealB)]);
        if (!cancelled) { setDataA(a); setDataB(b); }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };

    async function fetchScores(seg, reveal) {
      const params = seg.key === "_all"
        ? undefined
        : { filters: JSON.stringify({ [seg.key]: seg.value }), ...(reveal ? { reveal_small: true } : {}) };
      const { data } = await api.get(
        `/api/deployments/${deploymentId}/scores`,
        { params },
      );
      return data;
    }
  }, [deploymentId, aId, bId, segA, segB, revealA, revealB]);

  function setSegment(side, id) {
    setRevealed(new Set());
    onSetUrl(side === "a" ? "compare_a" : "compare_b", id);
  }

  function swap() {
    setRevealed(new Set());
    onSetUrl("compare_a", bId, { also: { compare_b: aId } });
  }

  if (loading) {
    return <div className="text-sm text-gray-500" data-testid="compare-loading">Loading…</div>;
  }
  if (error) {
    return <div className="text-sm" style={{ color: "#AD393E" }} data-testid="compare-error">{error}</div>;
  }

  return (
    <div data-testid="compare">
      <CompareHeader />
      <PickerBar
        segments={segments}
        aId={aId}
        bId={bId}
        onSetA={(id) => setSegment("a", id)}
        onSetB={(id) => setSegment("b", id)}
        onSwap={swap}
      />

      <ComparisonTable
        segA={segA}
        segB={segB}
        dataA={dataA}
        dataB={dataB}
        onReveal={setRevealTarget}
      />
      <ConfirmModal
        open={!!revealTarget}
        title="Reveal a small subgroup?"
        body={revealTarget ? `${revealTarget.key}: ${revealTarget.value} has fewer than five completed respondents. Showing these scores bypasses the n<5 anonymity rule, and the read is recorded in the admin audit log under your name.` : ""}
        confirmLabel="Reveal & log"
        onConfirm={() => {
          setRevealed((s) => new Set(s).add(revealTarget.id));
          setRevealTarget(null);
        }}
        onClose={() => setRevealTarget(null)}
        testId="compare-reveal-modal"
      />
    </div>
  );
}

function CompareHeader() {
  return (
    <div data-testid="compare-header">
      <div className="eyebrow mb-1">Compare</div>
      <h3 className="text-lg mb-3" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        Two segments side by side.
      </h3>
      <p className="text-sm text-gray-700 max-w-prose">
        Useful for testing whether a specific population is meaningfully different
        from the baseline. Segments with fewer than five respondents are
        suppressed on their own side; the comparison column reads as &ldquo;&mdash;&rdquo; when
        either side is suppressed. Small subgroups (like a leadership team) can be revealed by a
        consultant; every reveal is logged in the audit trail.
      </p>
    </div>
  );
}

function PickerBar({ segments, aId, bId, onSetA, onSetB, onSwap }) {
  return (
    <div className="flex items-center gap-4 mt-6 mb-8 flex-wrap" data-testid="compare-picker">
      <SegmentSelect label="A" segments={segments} value={aId} onChange={onSetA} testId="compare-select-a" />
      <button
        onClick={onSwap}
        className="btn-quiet"
        data-testid="compare-swap"
        title="Swap A and B"
      >
        ⇄ swap
      </button>
      <SegmentSelect label="B" segments={segments} value={bId} onChange={onSetB} testId="compare-select-b" />
    </div>
  );
}

function SegmentSelect({ label, segments, value, onChange, testId }) {
  return (
    <label className="flex items-baseline gap-2">
      <span className="eyebrow" style={{ color: "#2F6B7C" }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field"
        style={{ minWidth: "240px", padding: "8px 12px" }}
        data-testid={testId}
        aria-label={`Segment ${label}`}
      >
        {segments.map((s) => (
          <option key={s.id} value={s.id}>
            {s.id === "_all" ? s.value : `${s.key}: ${s.value}`}
          </option>
        ))}
      </select>
    </label>
  );
}

function ComparisonTable({ segA, segB, dataA, dataB, onReveal }) {
  const aSuppressed = !!dataA?.suppressed;
  const bSuppressed = !!dataB?.suppressed;
  const sameSegment = segA.id === segB.id;

  return (
    <div className="border border-gray-100" data-testid="compare-table">
      {/* Header row: A label / B label */}
      <div className="grid grid-cols-[1.5fr_1fr_1fr_0.75fr] bg-cream border-b border-gray-100">
        <div className="px-4 py-3">
          <div className="eyebrow text-gray-500">Dimension</div>
        </div>
        <SegmentHeader seg={segA} data={dataA} side="A" onReveal={onReveal} />
        <SegmentHeader seg={segB} data={dataB} side="B" onReveal={onReveal} />
        <div className="px-4 py-3 text-right">
          <div className="eyebrow text-gray-500">Δ</div>
        </div>
      </div>

      {sameSegment ? (
        <div className="px-4 py-6 text-sm text-gray-700" data-testid="compare-same-segment">
          A and B point at the same segment. Pick a different segment for B to compare.
        </div>
      ) : DIMENSIONS.map((dKey) => (
        <DimensionRow
          key={dKey}
          dKey={dKey}
          dimA={!aSuppressed ? (dataA?.dimensions || []).find((x) => x.dimension === dKey) : null}
          dimB={!bSuppressed ? (dataB?.dimensions || []).find((x) => x.dimension === dKey) : null}
          aSuppressed={aSuppressed}
          bSuppressed={bSuppressed}
          segA={segA}
          segB={segB}
        />
      ))}
    </div>
  );
}

function SegmentHeader({ seg, data, side, onReveal }) {
  const isSuppressed = !!data?.suppressed;
  const canReveal = isSuppressed && seg.id !== "_all" && (data?.n ?? 0) > 0;
  return (
    <div className="px-4 py-3" data-testid={`compare-header-${side.toLowerCase()}`}>
      <div className="eyebrow mb-1" style={{ color: "#2F6B7C" }}>{side}</div>
      <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        {seg.id === "_all" ? "All respondents" : seg.value}
      </div>
      {seg.id !== "_all" && (
        <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
          {seg.key}
        </div>
      )}
      <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
        {isSuppressed ? `n=${data?.n} · suppressed` : `n=${data?.n ?? 0}`}
      </div>
      {data?.small_n_revealed && (
        <div
          className="text-xs mt-1 inline-block px-2 py-0.5"
          style={{ background: "#FBF3E2", color: "#8A6418", border: "1px solid #D9A441", fontFamily: "Oswald", letterSpacing: "0.08em" }}
          data-testid={`compare-revealed-${side.toLowerCase()}`}
        >
          Small group · read logged
        </div>
      )}
      {canReveal && (
        <button
          onClick={() => onReveal(seg)}
          className="text-xs mt-2 block underline"
          style={{ color: "#AD393E", fontFamily: "Oswald", letterSpacing: "0.08em" }}
          data-testid={`compare-reveal-${side.toLowerCase()}`}
        >
          Reveal small group (audited)
        </button>
      )}
    </div>
  );
}

function DimensionRow({ dKey, dimA, dimB, aSuppressed, bSuppressed, segA, segB }) {
  const eitherSuppressed = aSuppressed || bSuppressed;
  const meanDelta = !eitherSuppressed && dimA && dimB ? dimA.mean - dimB.mean : null;
  const tbDelta = !eitherSuppressed && dimA && dimB ? dimA.tb_pct - dimB.tb_pct : null;
  const canMark = !eitherSuppressed && segA.id !== segB.id;

  return (
    <div
      className="grid grid-cols-[1.5fr_1fr_1fr_0.75fr] border-t border-gray-100"
      data-testid={`compare-row-${dKey}`}
    >
      <div className="px-4 py-4">
        <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
          {dimensionLabel(dKey)}
        </div>
        <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
          {dimensionShort(dKey)}
        </div>
        {canMark && (
          <div className="mt-3">
            <MarkButton
              kind="dimension_pair"
              payload={{
                dimension: dKey,
                segment_a: { key: segA.id === "_all" ? "_all" : segA.key, value: segA.value },
                segment_b: { key: segB.id === "_all" ? "_all" : segB.key, value: segB.value },
              }}
              testIdSuffix={`compare-${dKey}`}
            />
          </div>
        )}
      </div>
      <Cell dim={dimA} suppressed={aSuppressed} testId={`compare-cell-a-${dKey}`} />
      <Cell dim={dimB} suppressed={bSuppressed} testId={`compare-cell-b-${dKey}`} />
      <DeltaCell meanDelta={meanDelta} tbDelta={tbDelta} testId={`compare-delta-${dKey}`} />
    </div>
  );
}

function Cell({ dim, suppressed, testId }) {
  if (suppressed) {
    return (
      <div className="px-4 py-4" data-testid={testId}>
        <div
          className="text-xs text-gray-500 px-3 py-2 inline-block"
          style={{
            background: "repeating-linear-gradient(45deg, #F5F5F5, #F5F5F5 4px, #FBFAF7 4px, #FBFAF7 8px)",
            border: "1px solid #EEEEEE",
            fontFamily: "Oswald",
            letterSpacing: "0.1em",
          }}
          aria-disabled="true"
        >
          n&lt;5
        </div>
      </div>
    );
  }
  if (!dim) {
    return <div className="px-4 py-4" data-testid={testId}><div className="text-xs text-gray-400">—</div></div>;
  }
  const c = rubricColor(dim.rubric_level);
  return (
    <div className="px-4 py-4" data-testid={testId}>
      <div className="flex items-baseline gap-3">
        <span style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: "1.25rem", color: "#1A1A1A" }}>
          {dim.mean.toFixed(2)}
        </span>
        <span className="text-xs text-gray-500">{Math.round(dim.tb_pct)}% TB</span>
      </div>
      <span
        className="inline-block mt-1 text-xs px-2 py-0.5"
        style={{
          background: c.bg, color: c.fg, fontFamily: "Oswald", letterSpacing: "0.05em",
          border: `1px solid ${c.solid}33`,
        }}
      >
        {dim.rubric_level}
      </span>
    </div>
  );
}

function DeltaCell({ meanDelta, tbDelta, testId }) {
  if (meanDelta == null) {
    return (
      <div className="px-4 py-4 text-right text-xs text-gray-400" data-testid={testId}>
        —
      </div>
    );
  }
  // Color the magnitude: meaningful divergence (> 0.3 in mean OR > 10 in TB) gets attention.
  const meaningful = Math.abs(meanDelta) > 0.3 || Math.abs(tbDelta) > 10;
  const meanColor = meanDelta > 0 ? "#4592A8" : meanDelta < 0 ? "#AD393E" : "#6F6F6F";
  const tbColor = tbDelta > 0 ? "#4592A8" : tbDelta < 0 ? "#AD393E" : "#6F6F6F";
  return (
    <div className="px-4 py-4 text-right" data-testid={testId}>
      <div style={{ fontFamily: "Oswald", fontWeight: meaningful ? 700 : 500, color: meanColor, fontSize: "1rem" }}>
        {meanDelta >= 0 ? "+" : "−"}
        {Math.abs(meanDelta).toFixed(2)}
      </div>
      <div className="text-xs mt-0.5" style={{ fontFamily: "Oswald", color: tbColor }}>
        {tbDelta >= 0 ? "+" : "−"}
        {Math.abs(Math.round(tbDelta))}% TB
      </div>
    </div>
  );
}
